/* Movatic WMS — Mobile Tasks (loop de recolha ADR-0033 F4b) | CesarFilho 932048016 */
import { useCallback, useRef, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import {
  ClipboardList,
  MapPin,
  Package,
  Camera,
  Keyboard,
  Check,
  Ban,
} from 'lucide-react'
import {
  confirmPick,
  finishTask,
  type TaskDTO,
  type ConfirmPickBody,
} from '@/api/workforce'
import { useTaskOffer } from '../components/TaskOfferProvider'
import { useKeyboardScanner } from '../hooks/useKeyboardScanner'
import CameraScanner from '../components/CameraScanner'
import { useFeedback } from '../hooks/useFeedback'

/** Linha da task (payload do backend, ADR-0030 F1d + ADR-0033 F4a). Campos sao
 *  SerializerMethodField -> acesso defensivo, podem vir null. `product` = code do SKU. */
type TaskLineDTO = {
  product?: number | string | null
  product_name?: string
  product_image?: string | null
  qty?: number | string
  location?: string
  reservation_id?: number
  sequence?: number
  pick_mode?: 'UNIT' | 'QUANTITY'
}

const num = (v: unknown, def = 0): number => {
  const n = Number(v)
  return Number.isFinite(n) ? n : def
}
const nf = new Intl.NumberFormat(undefined, { maximumFractionDigits: 3 })
const fmt = (v: unknown) => nf.format(num(v))

/** sanitiza o codigo lido (ENTER/BOM/ZWSP/controlos), molde do Scan.tsx. */
function sanitize(raw: string): string {
  if (!raw) return ''
  return (
    raw
      .replace(/[\r\n]+$/g, '')
      .replace(/\uFEFF/g, '')
      // eslint-disable-next-line no-control-regex -- intencional: controlos C0/C1
      .replace(/[\u0000-\u001F\u007F-\u009F]/g, '')
      .replace(/\u00A0/g, ' ')
      .replace(/[\u200B-\u200D\u2060]/g, '')
      .trim()
  )
}

/** UUID por gesto: secure-context -> crypto.randomUUID; fallback defensivo (http/IP). */
function uuid(): string {
  const c = (globalThis as any).crypto
  if (c?.randomUUID) return c.randomUUID()
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
}

/** Thumbnail de referencia (ADR-0034 decisao 5: "foto do produto ao picar", Product.image).
 *  Pequena, so' referencia visual para o operador — distinta das imagens GRANDES do catalogo-
 *  ecra. Placeholder gradiente (#1a2640->#243556, design-system do coletor) quando nao ha foto. */
function RefThumb({ src, alt }: { src?: string | null; alt?: string }) {
  return (
    <div
      className="shrink-0 w-14 h-14 rounded-lg overflow-hidden grid place-items-center"
      style={{ background: 'linear-gradient(135deg, #1a2640, #243556)' }}
    >
      {src ? (
        <img
          src={src}
          alt={alt || ''}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      ) : (
        <Package size={22} className="text-neutral-400" />
      )}
    </div>
  )
}

export default function Tasks() {
  // A OFERTA (popup + poll do inbox) vive no TaskOfferProvider (F7 pecas 2/3): aqui so' se
  // EXECUTA a task ja' aceite (activeTask). claimNext = botao manual "Proxima tarefa";
  // claimError = o erro do pull manual.
  const {
    activeTask: task,
    clearActive,
    claimNext,
    claiming,
    claimError,
  } = useTaskOffer()
  const [flash, setFlash] = useState<string | null>(null)
  const [closed, setClosed] = useState<Set<number>>(new Set()) // reservas fechadas (PICKED/SHORT)
  const [falta, setFalta] = useState<Record<number, number>>({}) // qty_short por reserva
  const [scanned, setScanned] = useState<string | null>(null) // SKU validado (modo QUANTITY)
  const [qtyInput, setQtyInput] = useState('')
  const [useCamera, setUseCamera] = useState(false)

  // idem_key fixa POR GESTO (modelo A): so' se regenera apos o backend responder
  // (onSuccess). Um re-scan/re-tap apos falha de REDE (onError) reusa a mesma chave
  // -> o backend (apply_pick F2 + confirm_pick F3) e' idempotente, nao duplo-abate.
  const idemRef = useRef<string | null>(null)
  const idemFor = () => (idemRef.current ??= uuid())
  const fb = useFeedback()

  const lines: TaskLineDTO[] = Array.isArray((task as any)?.lines)
    ? (task as any).lines
    : []
  const pickable = lines.filter((l) => l.reservation_id != null)
  const feitas = pickable.filter((l) =>
    closed.has(l.reservation_id as number),
  ).length
  const active =
    pickable.find((l) => !closed.has(l.reservation_id as number)) ?? null
  const mode: 'UNIT' | 'QUANTITY' =
    active?.pick_mode === 'UNIT' ? 'UNIT' : 'QUANTITY'
  const faltaDe = (l: TaskLineDTO) =>
    falta[l.reservation_id as number] ?? num(l.qty)

  const tt = (task as any)?.task_type
  const taskType: string | null =
    typeof tt === 'string' ? tt : (tt?.code ?? null)

  const resetLoop = () => {
    setClosed(new Set())
    setFalta({})
    setScanned(null)
    setQtyInput('')
    idemRef.current = null
    setFlash(null)
  }

  // -- finalizar (quando all_picked) ----------------------------------------
  const finalizar = useMutation({
    mutationFn: () => finishTask((task as TaskDTO).id),
    onSuccess: () => {
      clearActive() // fim da execucao -> volta a ocioso (o poll do provider retoma)
      resetLoop()
      setFlash('Tarefa concluida.')
    },
    onError: (e: any) =>
      setFlash(e?.response?.data?.detail || 'Erro ao finalizar a tarefa.'),
  })

  // -- confirmar uma recolha (parcial / unidade / esgotado) -----------------
  const confirmar = useMutation({
    mutationFn: (body: ConfirmPickBody) =>
      confirmPick((task as TaskDTO).id, body),
    onSuccess: (r: any) => {
      // o backend RESPONDEU -> o gesto esta' decidido. A idem_key so' protege
      // contra falha de REDE (onError); aqui regenera-se para o proximo gesto.
      idemRef.current = null
      setScanned(null)
      setQtyInput('')
      if (!r?.ok) {
        setFlash(r?.detail || 'Confirmacao nao aplicada.')
        fb.err()
        return
      }
      setFlash(null)
      const res = r.reservation_id as number
      const restante = num(r.qty_short)
      setFalta((prev) => ({ ...prev, [res]: restante }))
      if (r.short || restante <= 0) {
        setClosed((prev) => {
          const s = new Set(prev)
          s.add(res)
          return s
        })
      }
      if (r.short) fb.short()
      else fb.ok()
      if (r.all_picked) {
        fb.done()
        finalizar.mutate()
      }
    },
    onError: (e: any) => {
      // NAO limpa idemRef: o re-gesto (re-scan/re-tap) reusa a mesma chave.
      setFlash(
        e?.response?.data?.detail ||
          'Erro ao confirmar. Repita a leitura (mesma chave).',
      )
      fb.err()
    },
  })

  // -- leitura (scan camera/HID) --------------------------------------------
  const onScan = useCallback(
    (raw: string) => {
      if (!active || confirmar.isPending) return
      const code = sanitize(raw)
      if (!code) return
      // fail-closed: a leitura tem de bater com o produto da linha ativa.
      if (String(active.product ?? '') !== code) {
        setFlash(
          `Leitura "${code}" nao corresponde ao produto da linha (${active.product ?? '—'}).`,
        )
        fb.err()
        return
      }
      setFlash(null)
      if (mode === 'UNIT') {
        confirmar.mutate({
          reservation_id: active.reservation_id as number,
          qty: 1,
          idem_key: idemFor(),
        })
      } else {
        // QUANTITY: o scan VALIDA o SKU e revela a caixa de qty (nao confirma ja').
        setScanned(code)
      }
    },
    [active, mode, confirmar],
  )

  useKeyboardScanner(onScan)

  // -- gestos de botao ------------------------------------------------------
  const submitQty = () => {
    if (!active || confirmar.isPending) return
    const n = Number(qtyInput)
    if (!Number.isFinite(n) || n < 0) {
      setFlash('Quantidade invalida.')
      fb.err()
      return
    }
    // n === 0 -> o backend declara SHORT (esgotado declarado).
    confirmar.mutate({
      reservation_id: active.reservation_id as number,
      qty: n,
      idem_key: idemFor(),
    })
  }

  const declararEsgotado = () => {
    if (!active || confirmar.isPending) return
    // "nao ha mais aqui" = SHORT. Passo idemFor() por UNIFORMIDADE do modelo de gesto
    // (e' inofensivo: o confirm_pick ja' e' idempotente neste ramo via guard de estado
    //  -- 2a chamada cai em state != ACTIVE -> {ok:false, already}; a chave nao abate nada).
    confirmar.mutate({
      reservation_id: active.reservation_id as number,
      qty: 0,
      idem_key: idemFor(),
    })
  }

  const busy = confirmar.isPending || finalizar.isPending

  return (
    <div className="py-3 grid gap-3">
      {/* O popup da oferta e' GLOBAL (TaskOfferProvider) -> nao se monta aqui. */}
      {/* topo: titulo + reclamar proxima tarefa (pull manual) */}
      <div className="card p-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <ClipboardList size={18} />
          <span className="font-semibold">Tarefas</span>
        </div>
        <button
          className="btn"
          onClick={() => claimNext()}
          disabled={claiming || busy}
          aria-label="Reclamar a proxima tarefa"
        >
          {claiming ? 'A reclamar...' : 'Proxima tarefa'}
        </button>
      </div>

      {claimError && (
        <div className="card p-3 text-sm text-neutral-600">{claimError}</div>
      )}
      {flash && <div className="card p-3 text-sm text-amber-700">{flash}</div>}

      {!task && !claimError && (
        <div className="card p-3 text-sm text-neutral-500">
          Toque em &quot;Proxima tarefa&quot; para comecar.
        </div>
      )}

      {task && (
        <div className="card p-3 grid gap-3">
          <div className="flex items-center justify-between gap-2">
            <span className="font-semibold">Tarefa #{task.id}</span>
            <span className="text-xs text-neutral-500">
              {[taskType, task.status].filter(Boolean).join(' · ') || '—'}
              {pickable.length > 0 && (
                <span className="ml-2 tabular-nums">
                  {feitas}/{pickable.length} linhas
                </span>
              )}
            </span>
          </div>

          {pickable.length === 0 ? (
            <div className="text-sm text-neutral-500">
              Esta tarefa nao tem linhas.
            </div>
          ) : (
            <div className="grid gap-2">
              {pickable.map((ln, i) => {
                const res = ln.reservation_id as number
                const isClosed = closed.has(res)
                const isActive = active?.reservation_id === res
                return (
                  <div
                    key={res ?? i}
                    className={
                      'card p-3 flex items-center justify-between gap-3' +
                      (isActive ? ' ring-2 ring-blue-500' : '') +
                      (isClosed ? ' opacity-60' : '')
                    }
                  >
                    <div className="min-w-0 grid gap-1">
                      <div className="flex items-center gap-2 text-sm">
                        <MapPin size={16} className="shrink-0" />
                        <span className="font-medium truncate">
                          {ln.location || '—'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-neutral-600">
                        <Package size={16} className="shrink-0" />
                        <span className="truncate">
                          {ln.product_name || ln.product || '—'}
                        </span>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      {isClosed ? (
                        <div className="flex items-center gap-1 text-green-600 text-sm">
                          <Check size={16} /> feito
                        </div>
                      ) : (
                        <>
                          <div className="text-xs text-neutral-500">Falta</div>
                          <div className="font-semibold tabular-nums">
                            {fmt(faltaDe(ln))}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {/* painel de recolha da linha ativa */}
          {active && (
            <div className="card p-3 grid gap-3 border-blue-200">
              {/* thumbnail de referencia (foto do produto ao picar) + nome/qty/local */}
              <div className="flex items-center gap-3">
                <RefThumb
                  src={active.product_image}
                  alt={active.product_name || String(active.product ?? '')}
                />
                <div className="min-w-0 flex-1 grid gap-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium truncate">
                      A recolher: {active.product_name || active.product}
                    </span>
                    <span className="shrink-0 text-xs rounded bg-neutral-100 px-2 py-0.5">
                      {mode === 'UNIT' ? 'Unidade' : 'Quantidade'}
                    </span>
                  </div>
                  <div className="text-sm text-neutral-600">
                    Falta{' '}
                    <span className="font-semibold tabular-nums">
                      {fmt(faltaDe(active))}
                    </span>{' '}
                    · local{' '}
                    <span className="font-medium">
                      {active.location || '—'}
                    </span>
                  </div>
                </div>
              </div>

              {/* toggle do leitor (camera/HID), molde do Scan.tsx */}
              <div className="flex items-center gap-2">
                <button
                  className="btn"
                  onClick={() => setUseCamera((v) => !v)}
                  aria-label="Alternar leitor camera/teclado"
                >
                  {useCamera ? <Keyboard size={16} /> : <Camera size={16} />}
                  <span className="ml-1">
                    {useCamera ? 'Usar leitor' : 'Usar camera'}
                  </span>
                </button>
                <span className="text-xs text-neutral-400">
                  {useCamera
                    ? 'Aponte a camera ao codigo'
                    : 'Leitor fisico ativo'}
                </span>
              </div>
              {useCamera && <CameraScanner onRead={onScan} />}

              {/* modo QUANTITY: caixa de qty (so' apos validar o SKU) */}
              {mode === 'QUANTITY' && scanned && (
                <div className="flex items-center gap-2">
                  <input
                    className="input"
                    type="number"
                    inputMode="decimal"
                    min={0}
                    autoFocus
                    value={qtyInput}
                    onChange={(e) => setQtyInput(e.target.value)}
                    placeholder="Quantidade (0 = esgotado)"
                    aria-label="Quantidade recolhida"
                  />
                  <button className="btn" onClick={submitQty} disabled={busy}>
                    Confirmar
                  </button>
                </div>
              )}

              {/* gesto de esgotado */}
              <button
                className="btn flex items-center gap-1 text-red-600"
                onClick={declararEsgotado}
                disabled={busy}
                aria-label="Declarar que nao ha mais neste endereco"
              >
                <Ban size={16} /> Nao ha mais aqui
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
